import { BotBase } from './botBase';
import type { BotBaseOptions } from './botBase';
import {
	choosePass,
	chooseTrickPlay,
	type ExpectiminimaxOptions,
	type ExpectiminimaxWorkerRequest,
	type ExpectiminimaxWorkerResponse,
} from './expectiminimax';

import type { Command, Manager } from '../lib';
import { GameAction, getRandomItem, makeNextStageCommand, RoundStage } from '../lib';

// How long to wait for the worker before falling back to inline search.
const WORKER_TIMEOUT_MS = 30_000;

export interface ExpectiminimaxBotOptions extends BotBaseOptions, ExpectiminimaxOptions {
	/** Optional web worker that runs the search off the main thread. */
	searchWorker?: Worker;
}

export function getExpectiminimaxCommand(
	game: Manager,
	position: number,
	autoAdvanceRound = false,
	options: ExpectiminimaxOptions = {},
): Command | null {
	const state = game.gameState;
	const valid = game.getValidActions(position);

	switch (state.roundStage) {
		case RoundStage.TrickEnd:
			return makeNextStageCommand();
		case RoundStage.RoundEnd:
			return autoAdvanceRound ? makeNextStageCommand() : getRandomItem(valid);
		case RoundStage.Trick:
			return chooseTrickPlay(state, position, options) ?? (valid.length > 0 ? getRandomItem(valid) : null);
		case RoundStage.Pass:
			return choosePass(state, position, options) ?? (valid.length > 0 ? getRandomItem(valid) : null);
		case RoundStage.PassEnd:
			return autoAdvanceRound ? makeNextStageCommand() : null;
		case RoundStage.Deal:
			return autoAdvanceRound
				? ({
					command: GameAction.Deal,
				} as Command)
				: null;
		case RoundStage.GameOver:
			return autoAdvanceRound
				? ({
					command: GameAction.NewGame,
				} as Command)
				: null;
		default:
			return valid.length > 0 ? getRandomItem(valid) : null;
	}
}

export class ExpectiminimaxBot extends BotBase {
	private readonly searchOptions: ExpectiminimaxOptions;
	private searchWorker?: Worker;
	private cachedCommand: Command | null = null;
	private pendingWorkerResolve: ((command: Command | null) => void) | null = null;
	private pendingWorkerId = 0;
	private workerRequestId = 0;

	constructor(options: ExpectiminimaxBotOptions) {
		const {
			maxDepth,
			timeBudgetMs,
			topK,
			passCandidateCap,
			passSearchDepth,
			searchWorker,
		} = options;
		super(options);
		this._isBot = true;
		this.searchOptions = {
			...(maxDepth !== undefined ? {
				maxDepth, 
			} : {}),
			...(timeBudgetMs !== undefined ? {
				timeBudgetMs, 
			} : {}),
			...(topK !== undefined ? {
				topK, 
			} : {}),
			...(passCandidateCap !== undefined ? {
				passCandidateCap, 
			} : {}),
			...(passSearchDepth !== undefined ? {
				passSearchDepth, 
			} : {}),
		};

		if (searchWorker) {
			this.searchWorker = searchWorker;
			searchWorker.addEventListener('message', this.onWorkerMessage);
			searchWorker.addEventListener('error', this.onWorkerError);
		}
	}

	public override async tick(game: Manager): Promise<void> {
		if (this.searchWorker) {
			const stage = game.gameState.roundStage;

			if (stage === RoundStage.Trick || stage === RoundStage.Pass) {
				try {
					this.cachedCommand = (await this.requestFromWorker(game)) ?? this.computeInline(game);
				} catch {
					this.disposeWorker();
					this.cachedCommand = this.computeInline(game);
				}
			} else {
				this.cachedCommand = this.computeInline(game);
			}

			// For a worker-backed bot there is a search in flight: skip the base
			// scheduling tick (which would consume this.cachedCommand via getCommand)
			// and instead wait for the worker result before acting.
			const command = this.cachedCommand;

			if (command) {
				this.scheduleAction(game);
			}

			return;
		}

		await super.tick(game);
	}

	public override doAction(game: Manager): Command | null {
		if (this.cachedCommand) {
			const command = this.cachedCommand;
			this.cachedCommand = null;
			game.action(command);
			return command;
		}

		return super.doAction(game);
	}

	public getCommand(game: Manager): Command | null {
		if (this.cachedCommand) {
			return this.cachedCommand;
		}

		if (this.searchWorker) {
			// A worker request is in flight; the delayed doAction fires once it
			// resolves, so don't make a blind move in the meantime.
			return null;
		}

		return this.computeInline(game);
	}

	private readonly onWorkerMessage = (ev: MessageEvent<ExpectiminimaxWorkerResponse>): void => {
		if (!this.pendingWorkerResolve) {
			return;
		}

		const msg = ev.data;

		if (!msg || msg.id !== this.pendingWorkerId) {
			return;
		}

		this.pendingWorkerResolve(msg.command);
		this.pendingWorkerResolve = null;
	};
	private readonly onWorkerError = (): void => {
		this.disposeWorker();

		if (this.pendingWorkerResolve) {
			this.pendingWorkerResolve(null);
			this.pendingWorkerResolve = null;
		}
	};

	private disposeWorker(): void {
		const worker = this.searchWorker;

		if (!worker) {
			return;
		}

		worker.removeEventListener('message', this.onWorkerMessage);
		worker.removeEventListener('error', this.onWorkerError);
		worker.terminate();
		this.searchWorker = undefined;
	}

	private computeInline(game: Manager): Command | null {
		return getExpectiminimaxCommand(game, this.position, this.autoAdvanceRound, this.searchOptions);
	}

	private requestFromWorker(game: Manager): Promise<Command | null> {
		const worker = this.searchWorker;

		if (!worker) {
			return Promise.resolve(null);
		}

		const id = ++this.workerRequestId;
		const request: ExpectiminimaxWorkerRequest = {
			kind: 'expectiminimax',
			id,
			roundStage: game.gameState.roundStage,
			gameState: game.gameState,
			position: this.position,
			options: this.searchOptions,
		};

		return new Promise<Command | null>((resolve, reject) => {
			let settled = false;

			const timeoutId = setTimeout(() => {
				if (!settled) {
					settled = true;
					this.pendingWorkerResolve = null;
					reject(new Error('ExpectiminimaxBot worker timed out'));
				}
			}, WORKER_TIMEOUT_MS);

			this.pendingWorkerResolve = (command) => {
				if (!settled) {
					settled = true;
					clearTimeout(timeoutId);
					resolve(command);
				}
			};

			this.pendingWorkerId = id;
			worker.postMessage(request);
		});
	}
}
